import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL, BRAND } from "@/lib/api";
import { Phone, EnvelopeSimple, InstagramLogo, FacebookLogo, ArrowUpRight, List, X, MapPin } from "@phosphor-icons/react";

const links = [
  { to: "/", label: "Home", end: true, num: "01" },
  { to: "/services", label: "Services", num: "02" },
  { to: "/about", label: "About", num: "03" },
  { to: "/reviews", label: "Reviews", num: "04" },
  { to: "/gallery", label: "Gallery", num: "05" },
  { to: "/blog", label: "Journal", num: "06" },
  { to: "/faq", label: "FAQ", num: "07" },
  { to: "/locations", label: "Locations", num: "08" },
  { to: "/contact", label: "Contact", num: "09" },
];

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  // Theme defaults: home starts at top (transparent), other pages have dark hero → default dark.
  const [theme, setTheme] = useState(isHome ? "dark" : "dark");
  const [atTop, setAtTop] = useState(true);
  // Visibility: hide on scroll DOWN, reveal on scroll UP (Twitter/Instagram pattern).
  // At-top is always visible regardless of direction.
  const [visible, setVisible] = useState(true);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Observe sections with [data-nav-theme] and flip the nav theme as they
  // cross under the header. Also handle the show-on-scroll-up / hide-on-
  // scroll-down behaviour. State updates only happen from scroll/resize
  // event handlers — never synchronously in the effect body.
  useEffect(() => {
    let raf = 0;
    let lastY = window.scrollY || 0;
    const NAV_BOTTOM = 80;
    const TOP_THRESHOLD = 30;
    const HIDE_AFTER = 120;
    const DELTA = 6;

    const compute = () => {
      raf = 0;
      const y = window.scrollY || 0;
      const top = y < TOP_THRESHOLD;
      setAtTop(top);

      if (top) {
        setVisible(true);
      } else if (y - lastY > DELTA && y > HIDE_AFTER) {
        setVisible(false);
      } else if (lastY - y > DELTA) {
        setVisible(true);
      }
      lastY = y;

      const sections = document.querySelectorAll("[data-nav-theme]");
      if (!sections.length) {
        setTheme("light");
        return;
      }
      let active = null;
      for (const el of sections) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= NAV_BOTTOM && rect.bottom > NAV_BOTTOM) { active = el; break; }
      }
      if (!active) active = sections[0];
      const next = active.getAttribute("data-nav-theme") === "dark" ? "dark" : "light";
      setTheme((prev) => (prev === next ? prev : next));
    };

    const scheduleCompute = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    window.addEventListener("scroll", scheduleCompute, { passive: true });
    window.addEventListener("resize", scheduleCompute, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", scheduleCompute);
      window.removeEventListener("resize", scheduleCompute);
    };
  }, [loc.pathname]);

  const isDark = theme === "dark";
  // Nav is fully transparent across the entire site. No solid ribbon, no
  // blurred backdrop — just text + logo that flip colour based on the
  // [data-nav-theme] of the section underneath them. A super-faint hairline
  // appears once scrolled so the nav stays separable over busy backgrounds.
  const navBg = atTop
    ? "bg-transparent border-b border-transparent"
    : (isDark
        ? "bg-transparent border-b border-white/10"
        : "bg-transparent border-b border-slate-900/10");

  return (
    <>
      <header
        data-testid="site-nav"
        data-theme={theme}
        data-at-top={atTop ? "true" : "false"}
        data-visible={visible ? "true" : "false"}
        className={`fixed top-0 left-0 right-0 z-40 transition-[transform,opacity] duration-300 ease-out ${
          visible ? "translate-y-0 opacity-100 pointer-events-auto" : "-translate-y-full opacity-0 pointer-events-none"
        } ${navBg}`}
      >
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/quote" className={`pill-light ${isDark ? "!bg-white !text-navy" : ""}`} data-testid="nav-cta-book">
            Book now <ArrowUpRight size={14} weight="bold" />
          </Link>

          <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center pointer-events-auto" data-testid="site-logo">
            <img
              src={LOGO_URL}
              alt={BRAND.name}
              className={`h-9 md:h-11 transition-[filter,opacity] duration-500 ${isDark ? "opacity-95" : "invert opacity-90"}`}
            />
          </Link>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="hidden xl:flex items-center gap-3.5 mr-2">
              <a href={BRAND.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className={`transition-colors ${isDark ? "text-white/75 hover:text-white" : "text-navy/70 hover:text-navy"}`}><InstagramLogo size={16} weight="regular"/></a>
              <a href={BRAND.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className={`transition-colors ${isDark ? "text-white/75 hover:text-white" : "text-navy/70 hover:text-navy"}`}><FacebookLogo size={16} weight="regular"/></a>
              <a href={`mailto:${BRAND.email}`} aria-label="Email" className={`transition-colors ${isDark ? "text-white/75 hover:text-white" : "text-navy/70 hover:text-navy"}`}><EnvelopeSimple size={16} weight="regular"/></a>
            </div>
            <button data-testid="menu-btn" onClick={()=>setOpen(true)} className={`flex items-center gap-3 text-xs uppercase tracking-[0.24em] font-medium transition-colors ${isDark ? "text-white hover:text-white/80" : "text-navy hover:text-navy-deep"}`}>
              <span className="hidden md:inline">Menu</span>
              <span className="flex flex-col gap-1">
                <span className={`w-6 h-px ${isDark ? "bg-white" : "bg-navy"}`}/>
                <span className={`w-6 h-px ${isDark ? "bg-white" : "bg-navy"}`}/>
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 bg-navy-deep overflow-y-auto"
          >
            <div className="absolute inset-0 grain opacity-50 pointer-events-none"/>
            <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 py-6">
              <div className="flex items-center justify-between">
                <Link to="/quote" onClick={()=>setOpen(false)} className="pill-ghost">Book now <ArrowUpRight size={14} weight="bold"/></Link>
                <button onClick={()=>setOpen(false)} className="flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-white" data-testid="menu-close"><span>Close</span><X size={18}/></button>
              </div>
              <nav className="mt-16 md:mt-24 max-w-3xl">
                {links.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.05, duration: 0.5, ease: [0.22,1,0.36,1] }}
                    className="border-b border-white/10"
                  >
                    <NavLink to={l.to} end={l.end} onClick={()=>setOpen(false)}
                      className={({isActive}) => `group flex items-baseline justify-between py-5 md:py-6 ${isActive ? "text-white" : "text-white/70 hover:text-white"} transition-colors`}>
                      <span className="flex items-baseline gap-5 md:gap-8">
                        <span className="text-[10px] font-medium tracking-[0.24em] text-white/40">{l.num}</span>
                        <span className="font-display tracking-wide" style={{fontSize: 'clamp(2rem, 6vw, 4.5rem)'}}>{l.label}</span>
                      </span>
                      <ArrowUpRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity text-white/50" weight="thin"/>
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }}
                className="mt-16 md:mt-24 grid md:grid-cols-3 gap-8 text-sm text-white/70 pb-12">
                <div>
                  <div className="eyebrow !text-white/40 mb-3">Phone</div>
                  <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="hover:text-white text-base font-light">{BRAND.phone}</a>
                </div>
                <div>
                  <div className="eyebrow !text-white/40 mb-3">Email</div>
                  <a href={`mailto:${BRAND.email}`} className="hover:text-white text-base font-light break-all">{BRAND.email}</a>
                </div>
                <div>
                  <div className="eyebrow !text-white/40 mb-3">Service area</div>
                  <span className="text-base font-light">{BRAND.area}</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="relative bg-navy-deep text-white/70 overflow-hidden mt-32">
      <div className="absolute inset-0 grain opacity-50"/>
      <div className="absolute -right-32 -bottom-32 w-[500px] h-[500px] opacity-[0.04]">
        <img src={LOGO_URL} alt="" className="w-full h-full object-contain compass-rotate"/>
      </div>
      <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 py-12 md:py-14">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow !text-white/40">True North Maintenance</div>
            <h2 className="font-display text-white display-md mt-3 leading-[0.95] tracking-wide">Ready when<br/>you are.</h2>
            <Link to="/quote" className="pill-light mt-7">Get a free quote <ArrowUpRight size={14} weight="bold"/></Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-[1fr_1.5fr_1fr] gap-8 text-sm">
            <div>
              <div className="eyebrow !text-white/40 mb-4">Pages</div>
              <ul className="space-y-2">
                <li><Link to="/services" className="hover:text-white">Services</Link></li>
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/reviews" className="hover:text-white">Reviews</Link></li>
                <li><Link to="/gallery" className="hover:text-white">Gallery</Link></li>
                <li><Link to="/blog" className="hover:text-white">Journal</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              </ul>
            </div>
            <div className="min-w-0">
              <div className="eyebrow !text-white/40 mb-4">Contact</div>
              <ul className="space-y-2">
                <li><a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="hover:text-white inline-flex items-center gap-1.5"><Phone size={12}/>{BRAND.phone}</a></li>
                <li className="min-w-0">
                  <a href={`mailto:${BRAND.email}`} className="hover:text-white flex items-center gap-1.5 text-[12px] sm:text-[13px] leading-tight" style={{ wordBreak: "keep-all", overflowWrap: "normal" }}>
                    <EnvelopeSimple size={12} className="shrink-0"/>
                    <span className="truncate" title={BRAND.email}>{BRAND.email}</span>
                  </a>
                </li>
                <li className="inline-flex items-center gap-1.5"><MapPin size={12}/>{BRAND.area}</li>
              </ul>
            </div>
            <div>
              <div className="eyebrow !text-white/40 mb-4">Follow</div>
              <ul className="space-y-2">
                <li><a href={BRAND.socials.instagram} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-2"><InstagramLogo size={14}/>Instagram</a></li>
                <li><a href={BRAND.socials.facebook} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-2"><FacebookLogo size={14}/>Facebook</a></li>
                <li><a href={BRAND.socials.google} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-2"><MapPin size={14}/>Google Business</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
          <div>© {new Date().getFullYear()} True North Maintenance.</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
            <Link to="/privacy" className="hover:text-white/70">Privacy</Link>
            <Link to="/terms" className="hover:text-white/70">Terms</Link>
            <Link to="/data-deletion" className="hover:text-white/70">Data deletion</Link>
            <Link to="/admin/login" className="hover:text-white/70">Admin</Link>
            <span>Albury / Wodonga · AU</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout({ children }) {
  // Nav is fully transparent across the site — every page's hero/content
  // paints all the way to the very top of the viewport, and the nav floats
  // over it. Each top section opts into a [data-nav-theme] so the nav
  // text/logo flip colour accordingly.
  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <SiteNav />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
